// dashboardUtils.js

const getBookLexile = (paragraph) => {
    if (!paragraph) return 0;
    const match = paragraph.match(/\((\d+)L\)/);
    return match ? parseInt(match[1], 10) : 0;
};

export const extractFilters = (students) => {
    const schools = [...new Set(students.map(s => s.school_location).filter(Boolean))];
    const classes = [...new Set(students.map(s => s.class).filter(Boolean))];
    const sessions = new Set();
    const quarters = new Set();
    const messages = new Set();

    students.forEach(s => {
        s.quarters?.forEach(q => {
            if (q.session) sessions.add(q.session);
            if (q.quarter) quarters.add(q.quarter);
            if (q.reader_message) messages.add(q.reader_message);
        });
    });

    return {
        schools: schools.sort(),
        classes: classes.sort(),
        sessions: [...sessions].sort(),
        quarters: [...quarters].sort(),
        messages: [...messages].sort()
    };
};

export const exportToCSV = (data, filename) => {
    if (!data || !data.length) return;
    const headers = Object.keys(data[0]);
    const csvRows = [headers.join(',')];

    for (const row of data) {
        const values = headers.map(header => {
            let val = row[header];
            if (val === null || val === undefined) val = '';
            const escaped = ('' + val).replace(/"/g, '""');
            return `"${escaped}"`;
        });
        csvRows.push(values.join(','));
    }

    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.setAttribute('href', url);
    a.setAttribute('download', `${filename}.csv`);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
};

export const processDashboardData = (students, filters, loggedinRole, loggedinLocation) => {
    // ====================== ROLE-BASED ACCESS CONTROL ======================
    let accessibleStudents = students;

    if (loggedinRole === "User") {
        // User role sees NOTHING
        return {
            kpi: {
                avgScore: 0,
                avgSpeed: 0,
                improvedPercent: 0,
                wellMatchedPercent: 0,
                messageCounts: {},
                totalAssessed: 0
            },
            trend: { labels: [], scores: [], speeds: [] },
            improvementCompare: { labels: [], scoreRates: [], speedRates: [] },
            schoolCompare: { labels: [], scores: [], speeds: [], raw: {} },
            heatmapMatrix: {},
            distributions: {
                histogramBins: { '0-100L': 0, '101-200L': 0, '201-300L': 0, '301-400L': 0, '401-500L': 0, '500L+': 0 },
                scatterSpeedLevel: [],
                bookMatchingScatter: [],
                scatterGrowth: []
            },
            exportData: {
                baseAssessments: [],
                improvementData: [],
                scatterGrowth: []
            }
        };
    }

    if (loggedinRole === "Admin" && loggedinLocation) {
        // Admin can only see their own location's data
        accessibleStudents = students.filter(s =>
            s.school_location === loggedinLocation
        );
    }

    // Super Admin sees everything → no filtering needed
    // Other roles (if any) will be treated as User by default
    // ============================================================================================================
    // 1. Filter Students
    let filteredStudents = accessibleStudents;
    // let filteredStudents = students;
    if (filters.school) {
        filteredStudents = filteredStudents.filter(s => s.school_location === filters.school);
    }
    if (filters.class) {
        filteredStudents = filteredStudents.filter(s => s.class === filters.class);
    }
    if (filters.search) {
        const term = filters.search.toLowerCase();
        filteredStudents = filteredStudents.filter(s => s.name?.toLowerCase().includes(term) || s.erpId?.toLowerCase().includes(term));
    }

    // Baseline metrics arrays
    let totalScore = 0;
    let totalSpeed = 0;
    let assessedCount = 0;

    let improvedScoreCount = 0;
    let wellMatchedCount = 0;
    let totalWithBooks = 0;
    let studentsWithTwoQuarters = 0;

    const messageCounts = {
        "Outstanding Reader": 0,
        "Very Good Reader": 0,
        "Good Reader": 0,
        "Reading needs improvement": 0,
        "Reading to be Prioritized": 0,
        "Poor": 0
    };

    let improvementByTime = {};
    const timeTracking = {};
    let globalSchoolTracking = {};
    let classSchoolMatrix = {};

    let histogramBins = { '0-100L': 0, '101-200L': 0, '201-300L': 0, '301-400L': 0, '401-500L': 0, '500L+': 0 };
    let scatterSpeedLevel = [];
    let bookMatchingScatter = [];
    let scatterGrowth = [];

    const exportData = {
        baseAssessments: [],
        improvementData: [],
        scatterGrowth: []
    };

    // 2. Iterate filtered students to compute all metrics comprehensively based on all valid quarters
    filteredStudents.forEach(s => {
        if (!s.quarters || s.quarters.length === 0) return;

        let validQuarters = s.quarters;
        if (filters.session) validQuarters = validQuarters.filter(q => q.session === filters.session);
        if (filters.quarter) validQuarters = validQuarters.filter(q => String(q.quarter) === String(filters.quarter));
        if (filters.message) validQuarters = validQuarters.filter(q => q.reader_message?.toLowerCase() === filters.message?.toLowerCase());

        const loc = s.school_location || "Unknown";
        const cls = s.class || "Unknown";

        if (!globalSchoolTracking[loc]) globalSchoolTracking[loc] = { sumScore: 0, sumSpeed: 0, count: 0, messages: {} };
        if (!classSchoolMatrix[cls]) classSchoolMatrix[cls] = {};
        if (!classSchoolMatrix[cls][loc]) classSchoolMatrix[cls][loc] = { sum: 0, count: 0 };

        validQuarters.forEach(q => {
            if (q.lexile_score !== undefined) {
                const score = q.lexile_score || 0;
                const speed = q.lexile || 0;
                let msg = q.reader_message || "Unknown";

                totalScore += score;
                totalSpeed += speed;
                assessedCount++;

                exportData.baseAssessments.push({
                    "Student Name": s.name,
                    "ERP ID": s.erpId,
                    "Class": s.class,
                    "School Location": loc,
                    "Session": q.session,
                    "Quarter": q.quarter,
                    "Lexile Score": score,
                    "Reading Speed (WPM)": speed,
                    "Reader Message": msg,
                    "Selected Paragraph": q.selected_paragraph || ""
                });

                // Standardize message
                let normalizedMsg = msg;
                if (msg.toLowerCase().includes("pe Prioritized")) normalizedMsg = "Reading to be Prioritized";

                if (messageCounts[normalizedMsg] !== undefined) {
                    messageCounts[normalizedMsg]++;
                } else if (messageCounts[msg] !== undefined) {
                    messageCounts[msg]++;
                } else {
                    messageCounts[msg] = 1;
                }

                // Trends over time
                const timeKey = `${q.session} Q${q.quarter}`;
                if (!timeTracking[timeKey]) timeTracking[timeKey] = { sumScore: 0, sumSpeed: 0, count: 0 };
                timeTracking[timeKey].sumScore += score;
                timeTracking[timeKey].sumSpeed += speed;
                timeTracking[timeKey].count++;

                // School compare
                globalSchoolTracking[loc].sumScore += score;
                globalSchoolTracking[loc].sumSpeed += speed;
                globalSchoolTracking[loc].count++;
                globalSchoolTracking[loc].messages[normalizedMsg] = (globalSchoolTracking[loc].messages[normalizedMsg] || 0) + 1;

                // Matrix
                classSchoolMatrix[cls][loc].sum += score;
                classSchoolMatrix[cls][loc].count++;

                // Dist Bins
                if (score <= 100) histogramBins['0-100L']++;
                else if (score <= 200) histogramBins['101-200L']++;
                else if (score <= 300) histogramBins['201-300L']++;
                else if (score <= 400) histogramBins['301-400L']++;
                else if (score <= 500) histogramBins['401-500L']++;
                else histogramBins['500L+']++;

                // Scatter Speed Level
                scatterSpeedLevel.push({ x: speed, y: score, msg: normalizedMsg, name: s.name, erpId: s.erpId, session: q.session, quarter: q.quarter });

                // Book Matching
                const bookL = getBookLexile(q.selected_paragraph);
                if (bookL > 0) {
                    totalWithBooks++;
                    if (Math.abs(score - bookL) <= 100) {
                        wellMatchedCount++;
                    }
                    bookMatchingScatter.push({ x: score, y: bookL, r: 5, name: s.name, erpId: s.erpId, session: q.session, quarter: q.quarter });
                }
            }
        });

        // Improvement logic (always based on sequential history regardless of single-quarter filter)
        if (s.quarters.length >= 2) {
            const sortedQs = [...s.quarters].sort((a, b) => {
                const sA = a.session || "";
                const sB = b.session || "";
                if (sA !== sB) return sA.localeCompare(sB);
                return Number(a.quarter) - Number(b.quarter);
            });

            const currentQ = sortedQs[sortedQs.length - 1];
            const prevQ = sortedQs[sortedQs.length - 2];

            studentsWithTwoQuarters++;

            const improvedScore = currentQ.lexile_score > prevQ.lexile_score;
            const improvedSpeed = currentQ.lexile > prevQ.lexile;

            if (improvedScore) improvedScoreCount++;

            const transKey = `${prevQ.session} Q${prevQ.quarter} -> ${currentQ.session} Q${currentQ.quarter}`;
            if (!improvementByTime[transKey]) improvementByTime[transKey] = { count: 0, improvedScore: 0, improvedSpeed: 0 };

            improvementByTime[transKey].count++;
            if (improvedScore) improvementByTime[transKey].improvedScore++;
            if (improvedSpeed) improvementByTime[transKey].improvedSpeed++;

            exportData.improvementData.push({
                "Student Name": s.name,
                "ERP ID": s.erpId,
                "Class": s.class,
                "School Location": loc,
                "Transition": transKey,
                "Prev Score": prevQ.lexile_score,
                "Curr Score": currentQ.lexile_score,
                "Score Improved?": improvedScore ? "Yes" : "No",
                "Prev Speed": prevQ.lexile,
                "Curr Speed": currentQ.lexile,
                "Speed Improved?": improvedSpeed ? "Yes" : "No"
            });

            const pScore = prevQ.lexile_score || 0;
            const cScore = currentQ.lexile_score || 0;
            scatterGrowth.push({ x: pScore, y: cScore, name: s.name, erpId: s.erpId });
            exportData.scatterGrowth.push({
                "Student Name": s.name,
                "ERP ID": s.erpId,
                "Prev Score (X)": pScore,
                "Curr Score (Y)": cScore
            });
        }
    });

    const kpi = {
        avgScore: assessedCount > 0 ? (totalScore / assessedCount).toFixed(0) : 0,
        avgSpeed: assessedCount > 0 ? (totalSpeed / assessedCount).toFixed(1) : 0,
        improvedPercent: studentsWithTwoQuarters > 0 ? ((improvedScoreCount / studentsWithTwoQuarters) * 100).toFixed(0) : 0,
        wellMatchedPercent: totalWithBooks > 0 ? ((wellMatchedCount / totalWithBooks) * 100).toFixed(0) : 0,
        messageCounts,
        totalAssessed: assessedCount
    };

    const timeLabels = Object.keys(timeTracking).sort();
    const timeScores = timeLabels.map(l => (timeTracking[l].sumScore / timeTracking[l].count).toFixed(0));
    const timeSpeeds = timeLabels.map(l => (timeTracking[l].sumSpeed / timeTracking[l].count).toFixed(0));

    const schoolLabels = Object.keys(globalSchoolTracking);
    const schAvgScores = schoolLabels.map(l => globalSchoolTracking[l].count > 0 ? (globalSchoolTracking[l].sumScore / globalSchoolTracking[l].count).toFixed(0) : 0);
    const schAvgSpeeds = schoolLabels.map(l => globalSchoolTracking[l].count > 0 ? (globalSchoolTracking[l].sumSpeed / globalSchoolTracking[l].count).toFixed(0) : 0);

    const impLabels = Object.keys(improvementByTime).slice(-5);
    const impScoreRates = impLabels.map(l => ((improvementByTime[l].improvedScore / improvementByTime[l].count) * 100).toFixed(0));
    const impSpeedRates = impLabels.map(l => ((improvementByTime[l].improvedSpeed / improvementByTime[l].count) * 100).toFixed(0));

    return {
        kpi,
        trend: { labels: timeLabels, scores: timeScores, speeds: timeSpeeds },
        improvementCompare: { labels: impLabels, scoreRates: impScoreRates, speedRates: impSpeedRates },
        schoolCompare: { labels: schoolLabels, scores: schAvgScores, speeds: schAvgSpeeds, raw: globalSchoolTracking },
        heatmapMatrix: classSchoolMatrix,
        distributions: { histogramBins, scatterSpeedLevel, bookMatchingScatter, scatterGrowth },
        exportData
    };
};
